import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useAdminApiService } from '../hooks/useAdminApiService';
import { apiService } from '../services/apiService';
import { adminAuthService } from '../services/adminAuthService';
import { setAdminUser } from '../store/slices/authSlice';
import QRCode from 'qrcode';
import { branding } from '../constants/branding';

const normalizeChain = (raw?: string) => {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'eth') return 'ethereum';
  if (v === 'matic') return 'polygon';
  if (v === 'btc') return 'bitcoin';
  if (v === 'xlm') return 'stellar';
  if (v === 'sol') return 'solana';
  if (v === 'ada') return 'cardano';
  return v;
};

function adminHasFullAccess(data: Record<string, unknown> | null): boolean {
  if (!data) return false;
  if (data.hasAccess === true) return true;
  const st = data.status as string | undefined;
  return st === 'ACTIVE' || st === 'GRACE_PERIOD' || st === 'PLATFORM_OWNER';
}

const AdminSubscriptionRenewalPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const adminUser = useAppSelector((s) => s.auth.adminUser);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const adminApi = useAdminApiService();

  const [loading, setLoading] = useState(true);
  const [adminSubscriptionStatus, setAdminSubscriptionStatus] = useState<any>(null);
  const [pendingAdminRenewal, setPendingAdminRenewal] = useState<any>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [renewPaymentMethod, setRenewPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [renewCryptoBlockchain, setRenewCryptoBlockchain] = useState('ethereum');
  const [renewCryptoSymbol, setRenewCryptoSymbol] = useState('USDT');
  const [adminPlans, setAdminPlans] = useState<
    Array<{ id: number; name: string; priceMonthly: number; currency: string; paymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO' }>
  >([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [renewTxHash, setRenewTxHash] = useState('');
  const [renewTxBusy, setRenewTxBusy] = useState(false);
  const [renewWalletBusy, setRenewWalletBusy] = useState(false);
  const [renewDestinationQr, setRenewDestinationQr] = useState<string | null>(null);
  const [renewOwnWalletAddress, setRenewOwnWalletAddress] = useState<string | null>(null);
  const [renewOwnWalletQr, setRenewOwnWalletQr] = useState<string | null>(null);

  const loadPendingAdminRenewal = useCallback(async () => {
    try {
      const renewPendingResponse = await adminApi.getAdminSubscriptionRenewPending();
      if (renewPendingResponse.success && renewPendingResponse.data) {
        setPendingAdminRenewal(renewPendingResponse.data);
      }
    } catch {
      /* ignore */
    }
  }, [adminApi]);

  useEffect(() => {
    if (!isAuthenticated || !adminUser) {
      navigate('/admin/login', { replace: true });
      return;
    }
    if (adminUser.role === 'SUPER_ADMIN') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const subResponse = await adminApi.getAdminSubscriptionStatus();
      const data = subResponse.success && subResponse.data ? (subResponse.data as Record<string, unknown>) : null;
      if (cancelled) return;
      if (adminHasFullAccess(data)) {
        navigate('/admin/dashboard', { replace: true });
        return;
      }
      setAdminSubscriptionStatus(data);
      await loadPendingAdminRenewal();
      const plansRes = await adminApi.getAdminSubscriptionPlans();
      if (!cancelled && plansRes.success && Array.isArray(plansRes.data)) {
        const mapped = (plansRes.data as any[])
          .map((plan) => ({
            id: Number(plan.id),
            name: String(plan.name ?? 'Plan'),
            priceMonthly: parseFloat(String(plan.priceMonthly ?? plan.price_monthly ?? '0').replace(',', '.')) || 0,
            currency: String(plan.currency ?? 'EUR'),
            paymentMethods: String(plan.registrationPaymentMethods || plan.registration_payment_methods || 'ALL').toUpperCase() as
              | 'ALL'
              | 'STRIPE'
              | 'CRYPTO',
            active: plan.is_active !== false && plan.active !== false,
          }))
          .filter((plan) => plan.active && Number.isFinite(plan.id));
        setAdminPlans(mapped);
        if ((data?.planId == null || data?.planId === '') && mapped.length > 0) {
          setSelectedPlanId(mapped[0].id);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, adminUser, navigate, adminApi, loadPendingAdminRenewal]);

  useEffect(() => {
    const stripe = (searchParams.get('stripe') || '').toLowerCase();
    const adminRenewal = searchParams.get('adminRenewal');
    if (stripe !== 'success' || adminRenewal !== '1' || !adminUser || adminUser.role === 'SUPER_ADMIN') return;
    const sessionId = searchParams.get('session_id');
    void (async () => {
      try {
        const res = await adminApi.confirmAdminSubscriptionRenewalPayment(sessionId ? { sessionId } : {});
        if (res.success && res.data) {
          setPendingAdminRenewal(res.data as any);
        } else {
          await loadPendingAdminRenewal();
        }
      } catch {
        await loadPendingAdminRenewal();
      }
      try {
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } catch {
        /* ignore */
      }
      navigate('/admin/subscription-renew', { replace: true });
    })();
  }, [searchParams, adminUser, adminApi, navigate, loadPendingAdminRenewal]);

  useEffect(() => {
    let canceled = false;
    const p = pendingAdminRenewal;
    if (!p?.hasPending || p.status !== 'AWAITING_PAYMENT' || String(p.paymentMethod || '').toUpperCase() !== 'CRYPTO') {
      setRenewDestinationQr(null);
      setRenewOwnWalletAddress(null);
      setRenewOwnWalletQr(null);
      return;
    }
    const destinationAddress = String(p.destinationAddress || p.cryptoDestinationAddress || '').trim();
    const chain = normalizeChain(String(p.blockchain || p.cryptoBlockchain || ''));
    void (async () => {
      try {
        if (destinationAddress) {
          const dqr = await QRCode.toDataURL(destinationAddress, { width: 160, margin: 2, errorCorrectionLevel: 'M' });
          if (!canceled) setRenewDestinationQr(dqr);
        }
      } catch {
        if (!canceled) setRenewDestinationQr(null);
      }
      try {
        const wallets = await apiService.get<Array<{ blockchain?: string; address?: string }>>('/crypto/wallet');
        if (canceled) return;
        const list = Array.isArray(wallets.data) ? wallets.data : [];
        const found = list.find((w) => normalizeChain(w.blockchain) === chain);
        if (found?.address) {
          setRenewOwnWalletAddress(found.address);
          try {
            const uqr = await QRCode.toDataURL(found.address, { width: 160, margin: 2, errorCorrectionLevel: 'M' });
            if (!canceled) setRenewOwnWalletQr(uqr);
          } catch {
            if (!canceled) setRenewOwnWalletQr(null);
          }
        } else {
          setRenewOwnWalletAddress(null);
          setRenewOwnWalletQr(null);
        }
      } catch {
        if (!canceled) {
          setRenewOwnWalletAddress(null);
          setRenewOwnWalletQr(null);
        }
      }
    })();
    return () => {
      canceled = true;
    };
  }, [pendingAdminRenewal]);

  const hasAssignedPlan =
    adminSubscriptionStatus?.planId != null && adminSubscriptionStatus?.planId !== '';
  const effectivePlanId = hasAssignedPlan
    ? Number(adminSubscriptionStatus?.planId)
    : selectedPlanId;
  const adminRenewEligible =
    !!adminSubscriptionStatus &&
    adminUser?.role !== 'SUPER_ADMIN' &&
    !pendingAdminRenewal?.hasPending &&
    adminSubscriptionStatus.hasAccess !== true &&
    (adminSubscriptionStatus.status === 'EXPIRED' ||
      adminSubscriptionStatus.status === 'SUSPENDED' ||
      adminSubscriptionStatus.status === 'CANCELLED' ||
      adminSubscriptionStatus.status === 'NONE') &&
    Number.isFinite(effectivePlanId as number);
  const selectedRenewalPlan =
    (effectivePlanId != null && Number.isFinite(effectivePlanId as number)
      ? adminPlans.find((plan) => plan.id === Number(effectivePlanId))
      : undefined) || null;
  const selectedPlanPaymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO' =
    selectedRenewalPlan?.paymentMethods || 'ALL';
  const allowedRenewPaymentMethods: Array<'STRIPE' | 'CRYPTO'> =
    selectedPlanPaymentMethods === 'STRIPE'
      ? ['STRIPE']
      : selectedPlanPaymentMethods === 'CRYPTO'
        ? ['CRYPTO']
        : ['STRIPE', 'CRYPTO'];

  useEffect(() => {
    if (allowedRenewPaymentMethods.includes(renewPaymentMethod)) return;
    setRenewPaymentMethod(allowedRenewPaymentMethods[0] || 'STRIPE');
  }, [allowedRenewPaymentMethods, renewPaymentMethod]);

  const startAdminRenewalFlow = async () => {
    const pid = effectivePlanId;
    if (pid == null || renewBusy || adminUser?.role === 'SUPER_ADMIN') return;
    setRenewBusy(true);
    setRenewError(null);
    try {
      const res = await adminApi.startAdminSubscriptionRenewal(
        Number(pid),
        renewPaymentMethod,
        renewPaymentMethod === 'CRYPTO' ? renewCryptoBlockchain : undefined,
        renewPaymentMethod === 'CRYPTO' ? renewCryptoSymbol : undefined
      );
      if (!res.success) {
        setRenewError((res as any).error || 'Renewal could not be started.');
        return;
      }
      const data = res.data as any;
      if (data?.requiresCompliance) {
        await loadPendingAdminRenewal();
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } else if (data?.checkoutUrl) {
        window.open(data.checkoutUrl as string, '_blank', 'noopener,noreferrer');
        await loadPendingAdminRenewal();
        const subRes = await adminApi.getAdminSubscriptionStatus();
        if (subRes.success && subRes.data) setAdminSubscriptionStatus(subRes.data);
      } else if (String(data?.paymentMethod || '').toUpperCase() === 'CRYPTO') {
        await loadPendingAdminRenewal();
      }
    } catch (err: any) {
      setRenewError(err?.message || 'Renewal could not be started.');
    } finally {
      setRenewBusy(false);
    }
  };

  const submitAdminRenewalCryptoProof = async () => {
    if (!pendingAdminRenewal?.renewalRequestId || !renewTxHash.trim()) return;
    setRenewTxBusy(true);
    setRenewError(null);
    try {
      const res = await adminApi.submitAdminSubscriptionCryptoPayment({
        renewalRequestId: Number(pendingAdminRenewal.renewalRequestId),
        txHash: renewTxHash.trim(),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Could not submit transaction hash.');
        return;
      }
      setRenewTxHash('');
      await loadPendingAdminRenewal();
    } catch (e: any) {
      setRenewError(e?.message || 'Could not submit transaction hash.');
    } finally {
      setRenewTxBusy(false);
    }
  };

  const payAdminRenewalWithWallet = async () => {
    if (!pendingAdminRenewal?.renewalRequestId) return;
    setRenewWalletBusy(true);
    setRenewError(null);
    try {
      const res = await adminApi.payAdminSubscriptionWithWallet({
        renewalRequestId: Number(pendingAdminRenewal.renewalRequestId),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Wallet payment failed.');
        return;
      }
      await loadPendingAdminRenewal();
    } catch (e: any) {
      setRenewError(e?.message || 'Wallet payment failed.');
    } finally {
      setRenewWalletBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminAuthService.logout();
      dispatch(setAdminUser(null));
      navigate('/admin/login', { replace: true });
    } catch {
      navigate('/admin/login', { replace: true });
    }
  };

  const checkoutUrl = pendingAdminRenewal?.checkoutUrl as string | undefined;
  const st = adminSubscriptionStatus?.status as string | undefined;
  const statusPillClass =
    st === 'ACTIVE' || st === 'GRACE_PERIOD' || st === 'PLATFORM_OWNER'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : st === 'NONE'
        ? 'bg-slate-100 text-slate-700 border-slate-200'
        : 'bg-amber-100 text-amber-700 border-amber-200';

  if (loading || !adminSubscriptionStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: branding.pageBackground }}>
        <p className="font-nunito text-gray-600">Loading subscription…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: branding.pageBackground }}>
      <header className="bg-white border-b border-gray-200 px-6 py-4 md:px-8 flex justify-between items-center">
        <h1 className="text-2xl font-nunito font-bold text-gray-900">Admin subscription</h1>
        <button
          type="button"
          onClick={() => void handleLogout()}
          className="px-4 py-2 rounded-full border border-gray-300 text-sm font-mukta font-semibold text-pink-600 hover:bg-gray-50 transition-colors"
        >
          Log out
        </button>
      </header>
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 md:px-6 py-8 md:py-10 space-y-6">
        <p className="text-base font-nunito text-gray-700">
          Your admin portal is limited to this page until your subscription is active again.
        </p>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="text-xs font-nunito font-semibold text-gray-500 uppercase tracking-wider">Subscription overview</div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-mukta text-gray-500">Current plan</div>
              <div className="font-nunito text-2xl font-bold text-gray-900">{adminSubscriptionStatus.planName || '—'}</div>
            </div>
            <span className={`px-3 py-1 rounded-full border text-xs font-semibold font-mukta ${statusPillClass}`}>
              {st || 'Unknown'}
            </span>
          </div>
          <div className="text-sm font-mukta text-gray-700">
            Access type: <strong>{adminSubscriptionStatus.hasAccess === true ? 'Active' : 'Restricted'}</strong>
          </div>
        </div>

        {pendingAdminRenewal?.hasPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 space-y-3">
            <div className="font-nunito text-lg font-bold text-blue-900">Renewal in progress</div>
            <p className="text-sm text-blue-900 font-mukta">{pendingAdminRenewal.message || 'Complete the pending step.'}</p>
            {pendingAdminRenewal.status === 'AWAITING_PAYMENT' &&
              String(pendingAdminRenewal.paymentMethod || '').toUpperCase() === 'STRIPE' &&
              checkoutUrl && (
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1 px-6 py-2.5 bg-blue-600 text-white rounded-full font-mukta text-sm font-semibold hover:opacity-95 transition-opacity"
                >
                  Open Stripe checkout
                </a>
              )}
            {pendingAdminRenewal.status === 'AWAITING_PAYMENT' &&
              String(pendingAdminRenewal.paymentMethod || '').toUpperCase() === 'CRYPTO' && (
                <div className="grid gap-4 mt-2">
                  <div className="text-xs text-blue-900 space-y-1 font-mukta">
                    <div>
                      <strong>Destination:</strong>{' '}
                      {String(pendingAdminRenewal.destinationAddress || pendingAdminRenewal.cryptoDestinationAddress || '—')}
                    </div>
                    {renewDestinationQr && <img src={renewDestinationQr} alt="Pay to" className="w-36 h-36 mt-2" />}
                  </div>
                  {renewOwnWalletAddress && (
                    <div className="text-xs text-blue-900 border-t border-blue-200 pt-3 font-mukta">
                      <div>
                        <strong>Your wallet:</strong> {renewOwnWalletAddress}
                      </div>
                      {renewOwnWalletQr && <img src={renewOwnWalletQr} alt="Your wallet" className="w-36 h-36 mt-2" />}
                      <button
                        type="button"
                        onClick={() => void payAdminRenewalWithWallet()}
                        disabled={renewWalletBusy}
                        className="mt-3 w-full py-2.5 rounded-full bg-blue-600 text-white text-sm font-mukta font-semibold disabled:opacity-50"
                      >
                        {renewWalletBusy ? 'Submitting…' : 'Pay with my Zogzila wallet'}
                      </button>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-nunito font-semibold text-blue-900 mb-1">Transaction hash</label>
                    <input
                      value={renewTxHash}
                      onChange={(e) => setRenewTxHash(e.target.value)}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm font-mukta"
                      placeholder="0x…"
                    />
                    <button
                      type="button"
                      onClick={() => void submitAdminRenewalCryptoProof()}
                      disabled={renewTxBusy || !renewTxHash.trim()}
                      className="mt-2 w-full py-2.5 rounded-full border border-blue-400 text-blue-900 text-sm font-mukta font-semibold disabled:opacity-50"
                    >
                      {renewTxBusy ? 'Submitting…' : 'Submit tx hash'}
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}

        {!pendingAdminRenewal?.hasPending && adminRenewEligible && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
            {!hasAssignedPlan && (
              <div>
                <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">Choose plan</label>
                <select
                  value={selectedPlanId ?? ''}
                  onChange={(e) => setSelectedPlanId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mukta text-sm bg-white"
                >
                  <option value="">Select…</option>
                  {adminPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} — {plan.priceMonthly} {plan.currency}/mo
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="block text-sm font-nunito font-semibold text-gray-700">Payment method</label>
            <select
              value={renewPaymentMethod}
              onChange={(e) => setRenewPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mukta text-sm bg-white"
            >
              {allowedRenewPaymentMethods.includes('STRIPE') && <option value="STRIPE">Stripe</option>}
              {allowedRenewPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto</option>}
            </select>
            {renewPaymentMethod === 'CRYPTO' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-nunito font-semibold text-gray-600 mb-1">Network</label>
                  <select
                    value={renewCryptoBlockchain}
                    onChange={(e) => setRenewCryptoBlockchain(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm font-mukta bg-white"
                  >
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                    <option value="bitcoin">Bitcoin</option>
                    <option value="stellar">Stellar</option>
                    <option value="solana">Solana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-nunito font-semibold text-gray-600 mb-1">Symbol</label>
                  <input
                    value={renewCryptoSymbol}
                    onChange={(e) => setRenewCryptoSymbol(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm font-mukta"
                    placeholder="USDT"
                  />
                </div>
              </div>
            )}
            {renewError && <p className="text-sm text-red-600 font-nunito">{renewError}</p>}
            <div className="pt-1 md:flex md:justify-end">
              <button
                type="button"
                onClick={() => void startAdminRenewalFlow()}
                disabled={renewBusy}
                className="px-8 py-3 rounded-full font-mukta font-semibold text-sm transition-opacity"
                style={{
                  width: '220px',
                  maxWidth: '100%',
                  backgroundColor: renewBusy ? '#d1d5db' : '#2563eb',
                  color: renewBusy ? '#374151' : '#ffffff',
                  cursor: renewBusy ? 'not-allowed' : 'pointer',
                  opacity: renewBusy ? 1 : 0.98,
                }}
              >
                {renewBusy ? 'Starting…' : 'Start renewal'}
              </button>
            </div>
          </div>
        )}

        {!pendingAdminRenewal?.hasPending && !adminRenewEligible && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm font-nunito text-amber-900">
            {(adminSubscriptionStatus.status === 'NONE' || adminSubscriptionStatus.planId == null) && adminPlans.length === 0
              ? 'No billable admin plan is assigned. Please contact your Super Admin.'
              : 'Renewal cannot be started from this account state. Please contact your Super Admin.'}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSubscriptionRenewalPage;
